/*
 * Copyright 2021 LABOR.digital
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Last modified: 2021.03.10 at 22:20
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { forEach } from '@labor-digital/helferlein';
import { Provider } from '../../Reactivity/Provider';
import { Binder } from '../Binding/Binder';
import { BitContext } from '../BitContext';
import { HmrRegistry } from '../HmrRegistry';
var Mount = /** @class */ (function () {
    function Mount(app) {
        /**
         * Keeps track if the mount is currently connected to the DOM or not
         * @protected
         */
        this._isConnected = false;
        this._contentFlushTimeout = 0;
        this._initialContent = '';
        this._app = app;
    }
    Object.defineProperty(Mount.prototype, "el", {
        /**
         * Returns the html element this mount is bound to
         */
        get: function () {
            if (this._onElGet) {
                this._onElGet();
            }
            return this._el;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Mount.prototype, "bit", {
        /**
         * Returns the instance of the bit or undefined if there is none currently active.
         */
        get: function () {
            return this._i;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Mount.prototype, "isConnected", {
        /**
         * Returns true if the bit is currently connected to the DOM, false if not
         */
        get: function () {
            return this._isConnected;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Connects the mounted html element with the configured bit class
     */
    Mount.prototype.connect = function (el) {
        return __awaiter(this, void 0, void 0, function () {
            var isNew;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this._isConnected = true;
                        this._el = el;
                        clearTimeout(this._contentFlushTimeout);
                        return [4 /*yield*/, this.instantiateBitIfRequired()];
                    case 1:
                        isNew = _a.sent();
                        setTimeout(function () { return __awaiter(_this, void 0, void 0, function () {
                            var ctx, react_1, binder_1;
                            var _this = this;
                            var _a, _b;
                            return __generator(this, function (_c) {
                                switch (_c.label) {
                                    case 0:
                                        if (!this._i || !this._el) {
                                            return [2 /*return*/];
                                        }
                                        if (!isNew) return [3 /*break*/, 4];
                                        ctx = this._i.$context;
                                        react_1 = ctx.reactivityProvider;
                                        binder_1 = ctx.binder;
                                        react_1.bind(this, this._i);
                                        this._onElGet = function () {
                                            react_1.domChangeDependency();
                                        };
                                        return [4 /*yield*/, binder_1.bind(this._i)];
                                    case 1:
                                        _c.sent();
                                        if (this._el._bitOnLoadQueue) {
                                            forEach(this._el._bitOnLoadQueue, function (c) { return c(_this._i); });
                                            delete this._el._bitOnLoadQueue;
                                        }
                                        // Bind listener to refresh the bindings when the domChange event was executed
                                        this.el.addEventListener('domChange', this._changeListener = function () { return __awaiter(_this, void 0, void 0, function () {
                                            var _a;
                                            return __generator(this, function (_b) {
                                                switch (_b.label) {
                                                    case 0: return [4 /*yield*/, binder_1.refresh()];
                                                    case 1:
                                                        _b.sent();
                                                        react_1.reactToDomChanged();
                                                        if ((_a = this._i) === null || _a === void 0 ? void 0 : _a.domChanged) {
                                                            this._i.domChanged();
                                                        }
                                                        return [2 /*return*/];
                                                }
                                            });
                                        }); });
                                        // Because this library is a hybrid that works with the actual dom
                                        // we must store the initial HTML content, so we can restore it when the component gets rebound.
                                        if (this._initialContent === '') {
                                            this._initialContent = this.el.innerHTML;
                                        }
                                        if (!((_a = this._i) === null || _a === void 0 ? void 0 : _a.mounted)) return [3 /*break*/, 3];
                                        return [4 /*yield*/, this._i.mounted()];
                                    case 2:
                                        _c.sent();
                                        _c.label = 3;
                                    case 3:
                                        react_1.executeStaticAutoRun();
                                        return [3 /*break*/, 6];
                                    case 4:
                                        if (!((_b = this._i) === null || _b === void 0 ? void 0 : _b.remounted)) return [3 /*break*/, 6];
                                        return [4 /*yield*/, this._i.remounted()];
                                    case 5:
                                        _c.sent();
                                        _c.label = 6;
                                    case 6: return [2 /*return*/];
                                }
                            });
                        }); });
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Disconnects this mount from a bit instance, by destroying the instance and restoring the initial dom node
     */
    Mount.prototype.disconnect = function (force) {
        var _this = this;
        var _a, _b;
        if (!this._i || !this._el) {
            return;
        }
        var keepAlive = (_a = this._el.getAttribute('keep-alive')) !== null && _a !== void 0 ? _a : false;
        if ((_b = this._i) === null || _b === void 0 ? void 0 : _b.unmounted) {
            this._i.unmounted();
        }
        if (!keepAlive || force) {
            HmrRegistry.unregisterMount(this._el);
            this._i.$destroy();
            this._el.innerHTML = this._initialContent;
            this._isConnected = false;
            delete this._onElGet;
            if (this._i.destroyed) {
                this._i.destroyed();
            }
            if (this._changeListener) {
                this._el.removeEventListener('domChange', this._changeListener);
                delete this._changeListener;
            }
            // We use a timeout workaround to make sure the content gets flushed after 200ms
            // meaning there is a time-frame where the content stays in place if the node gets moved in the dom tree
            this._contentFlushTimeout = setTimeout(function () {
                _this._initialContent = '';
            }, 200);
            delete this._i;
            delete this._el;
        }
    };
    /**
     * Internal helper to create a new instance of the bit class that is defined using the type attribute.
     * @protected
     */
    Mount.prototype.instantiateBitIfRequired = function () {
        return __awaiter(this, void 0, void 0, function () {
            var type, di, ctor, react, binder, context;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this._i || !this._el) {
                            return [2 /*return*/, false];
                        }
                        type = this._el.getAttribute('type');
                        if (!type || type === '') {
                            console.error('Missing required attribute "type" to define the bit type!');
                            return [2 /*return*/, Promise.resolve(false)];
                        }
                        di = this._app.di;
                        return [4 /*yield*/, di.bitRegistry.get(type)];
                    case 1:
                        ctor = _a.sent();
                        if (ctor === null) {
                            console.error('Invalid bit type given!', type);
                            return [2 /*return*/, false];
                        }
                        HmrRegistry.registerMount(this._el, ctor);
                        react = new Provider();
                        binder = new Binder(di.bindableList);
                        context = new BitContext(this, di, react, binder);
                        this._i = new ctor(context);
                        di.pluginLoader.extendBit(this._i);
                        if (!this._i.created) return [3 /*break*/, 3];
                        return [4 /*yield*/, this._i.created()];
                    case 2:
                        _a.sent();
                        _a.label = 3;
                    case 3: return [2 /*return*/, true];
                }
            });
        });
    };
    return Mount;
}());
export { Mount };
//# sourceMappingURL=Mount.js.map